import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteGetMessagesBySegment, useTrimSegmentPages } from '@sendible/frontendv2/src/models/calendar';
import {
  SegmentQueryState,
  SegmentSide,
  appendSegment,
  buildMessagesByDay,
  countRows,
  createInitialSegments,
  createOpenSegments,
  edgeProgress,
  isDateCoveredOpen,
  isDateLoadable,
  loadedDateRange,
  lastPageInfo,
  loadedDays,
  planEdgeStep,
  planTrim,
  queriesToRefill,
  segmentKey,
} from './segments';

type UseCalendarSegmentsParams = {
  // The date the list opens around.
  initialFocus: CalendarMonthString;
  // The date the list is reset around when the filters change.
  resetFocus: CalendarMonthString;
  filterKey: string | undefined;
  // When true there are no date rows without messages, so the list is a plain infinite list of messages
  // and the date range never moves. When false the list walks through time and shows every date.
  hideEmptyDates: boolean;
  isEnabled: boolean;
};

const createSegments = (focus: CalendarMonthString, hideEmptyDates: boolean) =>
  hideEmptyDates ? createOpenSegments(focus) : createInitialSegments(focus);

// Owns the date ranges (segments) of the daily view. Each segment is paged by its own infinite query
// and nothing is fetched on its own: a fetch only happens after the grid reports that the user is
// scrolling towards the start or the end of what is loaded, and it stops as soon as that produced
// new rows.
export const useCalendarSegments = ({ initialFocus, resetFocus, filterKey, hideEmptyDates, isEnabled }: UseCalendarSegmentsParams) => {
  const resetKey = `${filterKey}|${hideEmptyDates}`;
  // The segments belong to the filters and the mode they were made for.
  const [list, setList] = useState(() => ({ key: resetKey, hide: hideEmptyDates, segments: createSegments(initialFocus, hideEmptyDates) }));
  const { segments } = list;
  const setSegments = useCallback((next: CalendarSegment[]) => setList((current) => ({ ...current, segments: next })), []);
  // In the render where the filters or the mode change, the reset below has not run yet. The old segments must not
  // be fetched with the new filters, and they must be read as the kind of list they are.
  const isCurrent = list.key === resetKey;
  const [requestTick, setRequestTick] = useState(0);
  const segmentsRef = useRef(segments);
  const viewDateRef = useRef<CalendarMonthString | null>(null);
  // Per side: the number of rows loaded when the grid asked for more, or null when nothing is wanted.
  const wantedRef = useRef<Record<SegmentSide, number | null>>({ past: null, future: null });
  const totalRowsRef = useRef(0);
  // Rows that were loaded and then dropped by eviction. Added to the rows on screen so that loading
  // a segment while dropping an equally full one still counts as progress.
  const evictedRowsRef = useRef(0);
  const evictedKeysRef = useRef(new Set<string>());
  const statesRef = useRef<SegmentQueryState[]>([]);
  const messagesByDayRef = useRef<Record<string, CalendarMessage[]>>({});
  const slotsRef = useRef<Array<{ fetchPreviousPage: (options?: { cancelRefetch?: boolean }) => unknown }>>([]);
  // The side the grid last asked for more on. Pages are trimmed from the other end.
  const grewSideRef = useRef<SegmentSide | null>(null);
  // A trim that has been applied to the cache but not yet reached the render, so it is not applied twice.
  const pendingTrimRef = useRef<string | null>(null);
  const trimPages = useTrimSegmentPages();
  const trimPagesRef = useRef(trimPages);

  // A fixed number of slots because hooks cannot be called in a loop. Segments are bounded by MAX_SEGMENTS.
  const slot0 = useInfiniteGetMessagesBySegment(segments[0], isEnabled && isCurrent);
  const slot1 = useInfiniteGetMessagesBySegment(segments[1], isEnabled && isCurrent);
  const slot2 = useInfiniteGetMessagesBySegment(segments[2], isEnabled && isCurrent);
  const slot3 = useInfiniteGetMessagesBySegment(segments[3], isEnabled && isCurrent);
  const slot4 = useInfiniteGetMessagesBySegment(segments[4], isEnabled && isCurrent);
  const slot5 = useInfiniteGetMessagesBySegment(segments[5], isEnabled && isCurrent);
  const slots = [slot0, slot1, slot2, slot3, slot4, slot5];

  const states: SegmentQueryState[] = segments.map((segment, index) => ({
    segment,
    isSuccess: slots[index].isSuccess,
    isFetching: slots[index].isFetching,
    hasNextPage: Boolean(slots[index].hasNextPage),
    rowCount: countRows(slots[index].data?.pages),
    ...loadedDateRange(slots[index].data?.pages),
    // Only closed segments are trimmed page by page.
    ...(list.hide ? {} : { pageCount: slots[index].data?.pages.length ?? 0, ...lastPageInfo(slots[index].data?.pages, segments[index].direction) }),
    // Only an open list holds a window of pages, so only it needs to know which ones.
    ...(list.hide
      ? {
          firstPageParam: slots[index].data?.pageParams[0] as number | undefined,
          lastPageParam: slots[index].data?.pageParams[(slots[index].data?.pageParams.length ?? 1) - 1] as number | undefined,
          hasPreviousPage: Boolean(slots[index].hasPreviousPage),
        }
      : {}),
  }));
  const totalRows = states.reduce((total, state) => total + state.rowCount, 0);

  // The callbacks below run from scroll handlers, so they read the latest values through refs.
  const messagesByDay = useMemo(
    () => buildMessagesByDay(slots.slice(0, segments.length).map((slot) => slot.data?.pages)),
    [segments.length, slot0.data, slot1.data, slot2.data, slot3.data, slot4.data, slot5.data]
  );

  useEffect(() => {
    segmentsRef.current = segments;
    statesRef.current = states;
    messagesByDayRef.current = messagesByDay;
    slotsRef.current = slots;
    totalRowsRef.current = totalRows;
    trimPagesRef.current = trimPages;
  });

  // Only what changes which days are known. The list of days must keep its identity otherwise, because
  // the grid rebuilds its observers whenever it changes.
  const knownDaysKey = states.map((s) => `${segmentKey(s.segment)}:${s.isSuccess}:${s.hasNextPage}:${s.firstDate}:${s.lastDate}`).join('|');

  // Without empty dates the only days are the ones that have messages.
  const daysWithMessages = useMemo(() => (Object.keys(messagesByDay) as CalendarMonthString[]).sort(), [messagesByDay]);
  const knownDays = useMemo(() => loadedDays(states), [knownDaysKey]);
  const days = list.hide ? daysWithMessages : knownDays;

  // With empty dates shown, gives up the pages (and then the segments) furthest from where the list is
  // growing once there are more messages than the budget allows. See planTrim.
  const trimIfOverBudget = useCallback(() => {
    if (hideEmptyDates) return;

    const plan = planTrim(statesRef.current, grewSideRef.current, messagesByDayRef.current, viewDateRef.current);

    if (!plan) return;

    const state = statesRef.current[plan.index];
    const key = `${segmentKey(state.segment)}:${state.pageCount}`;

    if (pendingTrimRef.current === key) return;

    pendingTrimRef.current = key;

    if (plan.kind === 'page') {
      // The rows still count as progress for a request that is waiting, as with an evicted segment.
      evictedRowsRef.current += state.lastPageRows ?? 0;
      trimPagesRef.current(state.segment, (state.pageCount ?? 1) - 1);
    } else {
      evictedKeysRef.current.add(segmentKey(state.segment));
      evictedRowsRef.current += state.rowCount;
      setSegments(segmentsRef.current.filter((_, index) => index !== plan.index));
    }
  }, [hideEmptyDates]);

  const requestMore = useCallback(
    (side: SegmentSide) => {
      grewSideRef.current = side;

      if (wantedRef.current[side] !== null) {
        // Still waiting, for instance because a segment could not be added yet. Look again.
        setRequestTick((tick) => tick + 1);

        return;
      }

      wantedRef.current[side] = hideEmptyDates ? edgeProgress(statesRef.current, side) : totalRowsRef.current + evictedRowsRef.current;
      setRequestTick((tick) => tick + 1);
    },
    [hideEmptyDates]
  );

  const extendPastData = useCallback(() => requestMore('past'), [requestMore]);
  const extendFutureData = useCallback(() => requestMore('future'), [requestMore]);

  // An open list only holds a window of pages. When the viewport comes close to the gap left by the pages
  // that were dropped on the way out, they are fetched back, which drops pages at the far end instead.
  const refillGap = useCallback(() => {
    if (!hideEmptyDates) return;

    queriesToRefill(statesRef.current, messagesByDayRef.current, viewDateRef.current).forEach((index) =>
      slotsRef.current[index].fetchPreviousPage({ cancelRefetch: false })
    );
  }, [hideEmptyDates]);

  const reportViewDate = useCallback(
    (iso: CalendarMonthString | null) => {
      viewDateRef.current = iso;
      refillGap();
      // The viewport may have moved clear of pages that were too close to trim.
      trimIfOverBudget();

      // ...or clear of a segment that could not be dropped to make room for the one that was asked for.
      if (wantedRef.current.past !== null || wantedRef.current.future !== null) setRequestTick((tick) => tick + 1);
    },
    [refillGap, trimIfOverBudget]
  );

  // Jumping somewhere outside the loaded range starts again around the target instead of stretching
  // the range, which would load everything in between.
  // Returns whether the list was started again.
  const ensureDate = useCallback(
    (iso: CalendarMonthString) => {
      const isLoaded = hideEmptyDates ? isDateCoveredOpen(statesRef.current, iso) : isDateLoadable(statesRef.current, iso);

      if (isLoaded) return false;

      const next = createSegments(iso, hideEmptyDates);
      const current = segmentsRef.current;

      // The list has not caught up with the last jump yet, which is when a second request for the same
      // date arrives (the Today button asks twice). Starting again around the same date changes nothing.
      if (next.length === current.length && next.every((segment, index) => segmentKey(segment) === segmentKey(current[index]))) return false;

      segmentsRef.current = next;
      grewSideRef.current = null;
      wantedRef.current = { past: null, future: null };
      evictedRowsRef.current = 0;
      evictedKeysRef.current.clear();
      setSegments(next);

      return true;
    },
    [hideEmptyDates, setSegments]
  );

  // New filters, or switching between showing and hiding empty dates, start the list again around the date in view.
  useEffect(() => {
    if (list.key === resetKey) return;

    grewSideRef.current = null;
    wantedRef.current = { past: null, future: null };
    evictedRowsRef.current = 0;
    evictedKeysRef.current.clear();
    setList({ key: resetKey, hide: hideEmptyDates, segments: createSegments(viewDateRef.current ?? resetFocus, hideEmptyDates) });
  }, [list.key, resetKey, hideEmptyDates, resetFocus]);

  // The pages held are part of it because a window can swap one page for another without changing the rows.
  const signature = states
    .map((s) => `${segmentKey(s.segment)}:${s.isSuccess}:${s.isFetching}:${s.hasNextPage}:${s.rowCount}:${s.firstPageParam}:${s.lastPageParam}`)
    .join('|');

  useEffect(() => {
    (['past', 'future'] as SegmentSide[]).forEach((side) => {
      const baseline = wantedRef.current[side];

      if (baseline === null) return;

      // Rows arrived, so the content now extends beyond the edge. Wait for the next scroll.
      const progress = list.hide ? edgeProgress(states, side) : totalRows + evictedRowsRef.current;

      if (progress > baseline) {
        wantedRef.current[side] = null;

        return;
      }

      // A refill on the edge's own query drops the page this request was measured from. Measure from where
      // it is now, or the page that is fetched next would never count as progress.
      if (progress < baseline) wantedRef.current[side] = progress;

      const action = planEdgeStep(states, side);

      if (action.type === 'fetchNextPage') {
        // Never cancel a page that is already on its way, such as one a refill asked for.
        slots[side === 'past' ? 0 : states.length - 1].fetchNextPage({ cancelRefetch: false });
      } else if (action.type === 'append') {
        const next = appendSegment(segments, side, action.segment, viewDateRef.current);

        if (next !== segments) {
          states.forEach((state) => {
            const key = segmentKey(state.segment);

            if (!next.some((segment) => segmentKey(segment) === key) && !evictedKeysRef.current.has(key)) {
              evictedKeysRef.current.add(key);
              evictedRowsRef.current += state.rowCount;
            }
          });
          setSegments(next);

          // Empty dates are visible rows, so the list has grown even when the new segment holds no messages.
          if (!list.hide) wantedRef.current[side] = null;
        }
        // Otherwise there was no room, because the viewport is too close to the segment that would have to go.
        // The request stays and is looked at again as the viewport moves.
      } else if (action.type === 'end') {
        wantedRef.current[side] = null;
      }
    });
    // The signature stands in for the query results, the effect must not run on every render.
  }, [signature, requestTick]);

  // A refill that has landed may leave the viewport still close to the gap.
  useEffect(() => {
    refillGap();
  }, [signature, refillGap]);

  // New data may have taken the list over its budget, and one trim at a time brings it back under.
  useEffect(() => {
    pendingTrimRef.current = null;
    trimIfOverBudget();
  }, [signature, trimIfOverBudget]);

  const isFetching = states.some((state) => state.isFetching);
  const isLoading = slots.slice(0, segments.length).some((slot) => slot.isLoading);
  const reachedEnd = states.every((state) => state.isSuccess && !state.hasNextPage);

  return {
    segments,
    days,
    messagesByDay,
    rowCount: totalRows,
    isLoading,
    isFetching,
    // Whether anything could still turn up. Used to hold back the empty state while the pages are read.
    // With empty dates shown the list can always be scrolled further, so there is never an empty state.
    hasMore: isFetching || !list.hide || !reachedEnd,
    extendPastData,
    extendFutureData,
    ensureDate,
    reportViewDate,
  };
};
